import {
  type _Array,
  type _Boolean,
  type _Number,
  type _String,
  type Address,
  AddressComponentType,
  type Appointment,
  BooleanComponentType,
  type Checkbox,
  type CheckboxGroup,
  ComponentType,
  type DateInput,
  type DatePicker,
  type DateTimeInput,
  type DonationInput,
  type Dropdown,
  type FileType,
  type FileUpload,
  ImageFit,
  type InPersonOptions,
  type InputField,
  InputType,
  type MultilineAddress,
  NumberComponentType,
  type NumberInput,
  NumberOfColumns,
  type Payment,
  PaymentComponentType,
  type PaymentInput,
  type FixedPayment,
  type PhoneInput,
  type PhoneOptions,
  type ProductCheckboxGroup,
  type RadioGroup,
  type RadioGroupOption,
  type RatingInput,
  type Scheduling,
  SchedulingComponentType,
  type ServicesDropdown,
  type Signature,
  StringComponentType,
  type Tags,
  type TextInput,
  type TimeInput,
  UploadFileFormat,
  type VideoConferenceOptions,
  type WixFile,
  WixFileComponentType,
  ObjectArrayComponentType,
  type ObjectArray,
  type Repeater,
} from '@wix/ambassador-forms-v2-form-template/types';

import { type FieldsInputField as PlatformizedInputField } from '@wix/ambassador-forms-v4-form/types';
import { transformFieldToV1 } from './transform-field-to-v1';

const idKey = 'id';
const LabelKey = 'label';
const PlaceholderKey = 'placeholder';
const DescriptionKey = 'description';
const HideLabelKey = 'hideLabel';
const ShowDateLabelsKey = 'showDateLabels';
const HidePlaceholderKey = 'hidePlaceholder';
const Use24HourFormatKey = 'use24HourFormat';
const AddOtherKey = 'addOther';
const AddOtherLabelKey = 'addOtherLabel';
const NumberOfColumnsKey = 'numberOfColumns';
const AddOtherPlaceholderKey = 'addOtherPlaceholder';
const DefaultValueKey = 'defaultValue';
const ValueKey = 'value';
const OptionsKey = 'options';
const ButtonTextKey = 'buttonText';
const ValueSliderLimitKey = 'valueSliderLimit';
const UploadFileFormatsKey = 'uploadFileFormats';
const ExplanationTextKey = 'explanationText';
const MediaKey = 'media';
const UploadEnabledKey = 'uploadEnabled';
const FirstDayOfWeekKey = 'firstDayOfWeek';
const DefaultCountryCodeKey = 'defaultCountry';
const ImageResizeKey = 'imageResize';
const ShowFlagKey = 'showFlag';
const NameKey = 'name';
const DurationKey = 'duration';
const StaffIdsKey = 'staffIds';
const ManualApprovalRequiredKey = 'manualApprovalRequired';
const FormatKey = 'format';
const FormatDescriptionTypeKey = 'formatDescriptionType';
const LocationTypeKey = 'locationType';
const FormatDescriptionKey = 'formatDescription';
const LocationIdsKey = 'locationIds';
const RepeaterFieldsKey = 'fields';
const RepeaterLayoutKey = 'layout';

export function makeViewOfInputField(
  inputOptions: InputField | PlatformizedInputField,
): object {
  switch (inputOptions.inputType) {
    case InputType.STRING:
      return resolveStringComponent(inputOptions.stringOptions!);
    case InputType.NUMBER:
      return resolveNumberComponent(inputOptions.numberOptions!);
    case InputType.BOOLEAN:
      return resolveBooleanComponent(inputOptions.booleanOptions!);
    case InputType.ARRAY:
      return resolveArrayComponent(inputOptions.arrayOptions!);
    case InputType.PAYMENT:
      return resolvePaymentComponent(inputOptions.paymentOptions!);
    case InputType.WIX_FILE:
      return resolveWixFileComponent(inputOptions.wixFileOptions!);
    case InputType.ADDRESS:
      return resolveMultilineAddressComponent(inputOptions.addressOptions!);
    case InputType.MULTILINE_ADDRESS:
      return resolveMultilineAddressComponent(
        inputOptions.multilineAddressOptions!,
      );
    case InputType.SCHEDULING:
      return resolveSchedulingComponent(inputOptions.schedulingOptions!);
    case InputType.OBJECT_ARRAY:
      return resolveObjectArrayComponent(inputOptions.objectArrayOptions!);
    default:
      throw new Error(`Unknown input type ${inputOptions.inputType}`);
  }
}

function resolveWixFileComponent(wixFileComponent: WixFile): object {
  switch (wixFileComponent.componentType) {
    case WixFileComponentType.FILE_UPLOAD:
      return takeWixFileInputViewProperties(
        wixFileComponent.fileUploadOptions!,
        wixFileComponent.validation!,
      );
    case WixFileComponentType.SIGNATURE:
      return takeSignatureInputViewProperties(
        wixFileComponent.signatureOptions!,
      );
    default:
      throw new Error(
        `Unknown wix file component type: ${wixFileComponent.componentType}`,
      );
  }
}

function takeWixFileInputViewProperties(
  fileUpload: FileUpload,
  validation: FileType,
): object {
  const fileFormats = (validation.uploadFileFormats ?? []).map(
    (format: string) => {
      switch (format) {
        case UploadFileFormat.VIDEO:
          return 'Video';
        case UploadFileFormat.IMAGE:
          return 'Image';
        case UploadFileFormat.AUDIO:
          return 'Audio';
        case UploadFileFormat.DOCUMENT:
          return 'Document';
        case UploadFileFormat.ARCHIVE:
          return 'Archive';
        default:
          throw new Error(`Unknown file format: ${format}`);
      }
    },
  );

  return {
    [HideLabelKey]: !fileUpload.showLabel,
    [UploadFileFormatsKey]: fileFormats,
    [ValueSliderLimitKey]: validation.fileLimit,
    ...(fileUpload.description && { [DescriptionKey]: fileUpload.description }),
    ...(fileUpload.label && { [LabelKey]: fileUpload.label }),
    ...(fileUpload.buttonText && { [ButtonTextKey]: fileUpload.buttonText }),
    ...(fileUpload.explanationText && {
      [ExplanationTextKey]: fileUpload.explanationText,
    }),
  };
}

function takeSignatureInputViewProperties(signature: Signature): object {
  return {
    [UploadEnabledKey]: signature.imageUploadEnabled,
    [HideLabelKey]: !signature.showLabel,
    ...(signature.label && { [LabelKey]: signature.label }),
    ...(signature.description && { [DescriptionKey]: signature.description }),
  };
}

function resolveArrayComponent(arrayComponent: _Array): object {
  switch (arrayComponent.componentType) {
    case ComponentType.CHECKBOX_GROUP:
      return takeCheckboxGroupViewProperties(
        arrayComponent.checkboxGroupOptions!,
      );
    case ComponentType.SERVICES_CHECKBOX_GROUP:
      return takeCheckboxGroupViewProperties(
        arrayComponent.servicesCheckboxGroupOptions!,
      );
    case ComponentType.TAGS:
      return takeTagsViewProperties(arrayComponent.tagsOptions!);
    default:
      throw new Error(
        `Unknown array component type: ${arrayComponent.componentType}`,
      );
  }
}

function takeCheckboxGroupViewProperties(checkboxGroup: CheckboxGroup): object {
  return {
    [HideLabelKey]: !checkboxGroup.showLabel,
    [AddOtherKey]: checkboxGroup.customOption != null,
    [OptionsKey]: checkboxGroup.options!.map((option: any) =>
      makeOptionView(option),
    ),
    [NumberOfColumnsKey]: numericNumberOfColumns(
      checkboxGroup.numberOfColumns ?? NumberOfColumns.ONE,
    ),
    [DefaultValueKey]: checkboxGroup
      .options!.filter((option: any) => option.default)
      .map((option: any) => option.value),
    ...(checkboxGroup.description && {
      [DescriptionKey]: checkboxGroup.description,
    }),
    ...(checkboxGroup.label && { [LabelKey]: checkboxGroup.label }),
    ...(checkboxGroup.customOption && {
      [AddOtherLabelKey]: checkboxGroup.customOption.label,
      [AddOtherPlaceholderKey]: checkboxGroup.customOption.placeholder,
    }),
  };
}

function takeTagsViewProperties(tags: Tags): object {
  return {
    [HideLabelKey]: !tags.showLabel,
    [AddOtherKey]: tags.customOption != null,
    [OptionsKey]: tags.options!.map((option: any) => makeOptionView(option)),
    [NumberOfColumnsKey]: numericNumberOfColumns(
      tags.numberOfColumns ?? NumberOfColumns.ONE,
    ),
    [DefaultValueKey]: tags
      .options!.filter((option: any) => option.default)
      .map((option: any) => option.value),
    ...(tags.description && { [DescriptionKey]: tags.description }),
    ...(tags.label && { [LabelKey]: tags.label }),
    ...(tags.customOption && {
      [AddOtherLabelKey]: tags.customOption.label,
      [AddOtherPlaceholderKey]: tags.customOption.placeholder,
    }),
  };
}

function takeProductCheckboxGroupViewProperties(
  checkboxGroup: ProductCheckboxGroup,
): object {
  return {
    [HideLabelKey]: !checkboxGroup.showLabel,
    [ImageResizeKey]:
      checkboxGroup.imageFit === ImageFit.CONTAIN ? 'FIT' : 'CROP',
    [OptionsKey]: checkboxGroup.options!.map((option: any) =>
      makeOptionView(option),
    ),
    [DefaultValueKey]: checkboxGroup
      .options!.filter((option: any) => option.default)
      .map((option: any) => option.value),
    ...(checkboxGroup.description && {
      [DescriptionKey]: checkboxGroup.description,
    }),
    ...(checkboxGroup.label && { [LabelKey]: checkboxGroup.label }),
  };
}

function resolveBooleanComponent(booleanComponent: _Boolean): object {
  switch (booleanComponent.componentType) {
    case BooleanComponentType.CHECKBOX:
      return takeCheckBoxInputViewProperties(booleanComponent.checkboxOptions!);
    default:
      throw new Error(
        `Unknown boolean component type: ${booleanComponent.componentType}`,
      );
  }
}

function takeCheckBoxInputViewProperties(checkBox: Checkbox): object {
  return {
    ...(checkBox.label ? { [LabelKey]: checkBox.label } : {}),
    defaultValue: checkBox.checked,
  };
}

function resolveNumberComponent(numberComponent: _Number): object {
  switch (numberComponent.componentType) {
    case NumberComponentType.NUMBER_INPUT:
      return takeNumberInputViewProperties(numberComponent.numberInputOptions!);
    case NumberComponentType.RATING_INPUT:
      return takeRatingInputViewProperties(numberComponent.ratingInputOptions!);
    default:
      throw new Error(
        `Unknown number component type: ${numberComponent.componentType}`,
      );
  }
}

function takeNumberInputViewProperties(numberInput: NumberInput): object {
  return {
    [HideLabelKey]: !numberInput.showLabel,
    ...(numberInput.description && {
      [DescriptionKey]: numberInput.description,
    }),
    ...(numberInput.label && { [LabelKey]: numberInput.label }),
    ...(numberInput.placeholder && {
      [PlaceholderKey]: numberInput.placeholder,
    }),
  };
}

function takeRatingInputViewProperties(ratingInput: RatingInput): object {
  return {
    [HideLabelKey]: !ratingInput.showLabel,
    ...(ratingInput.defaultValue && {
      [DefaultValueKey]: ratingInput.defaultValue,
    }),
    ...(ratingInput.description && {
      [DescriptionKey]: ratingInput.description,
    }),
    ...(ratingInput.label && { [LabelKey]: ratingInput.label }),
  };
}

function resolveStringComponent(stringComponent: _String): object {
  switch (stringComponent.componentType) {
    case StringComponentType.TEXT_INPUT:
      return takeTextInputViewProperties(stringComponent.textInputOptions!);
    case StringComponentType.RADIO_GROUP:
      return takeRadioGroupViewProperties(stringComponent.radioGroupOptions!);
    case StringComponentType.DROPDOWN:
      return takeDropdownViewProperties(stringComponent.dropdownOptions!);
    case StringComponentType.DATE_TIME:
      return takeDateTimeViewProperties(stringComponent.dateTimeOptions!);
    case StringComponentType.DATE_INPUT:
      return takeDateInputViewProperties(stringComponent.dateInputOptions!);
    case StringComponentType.TIME_INPUT:
      return takeTimeInputViewProperties(stringComponent.timeInputOptions!);
    case StringComponentType.DATE_PICKER:
      return takeDatePickerViewProperties(stringComponent.datePickerOptions!);
    case StringComponentType.PHONE_INPUT:
      return takePhoneInputViewProperties(stringComponent.phoneInputOptions!);
    case StringComponentType.SERVICES_DROPDOWN:
      return takeServiceDropdownViewProperties(
        stringComponent.servicesDropdownOptions!,
      );
    default:
      throw new Error(
        `Unknown string component type: ${stringComponent.componentType}`,
      );
  }
}

function takeTextInputViewProperties(textInput: TextInput): object {
  return {
    [HideLabelKey]: !textInput.showLabel,
    ...(textInput.description && { [DescriptionKey]: textInput.description }),
    ...(textInput.label && { [LabelKey]: textInput.label }),
    ...(textInput.placeholder && { [PlaceholderKey]: textInput.placeholder }),
  };
}

function takeRadioGroupViewProperties(radioGroup: RadioGroup): object {
  const options: RadioGroupOption[] = radioGroup.options ?? [];
  return {
    [HideLabelKey]: !radioGroup.showLabel,
    [AddOtherKey]: radioGroup.customOption != null,
    [NumberOfColumnsKey]: numericNumberOfColumns(
      radioGroup.numberOfColumns ?? NumberOfColumns.ONE,
    ),
    [OptionsKey]: options.map((option: any) => makeOptionView(option)),
    ...(radioGroup.description && { [DescriptionKey]: radioGroup.description }),
    ...(radioGroup.label && { [LabelKey]: radioGroup.label }),
    ...(radioGroup.customOption && {
      [AddOtherLabelKey]: radioGroup.customOption.label,
      [AddOtherPlaceholderKey]: radioGroup.customOption.placeholder,
    }),
    ...(options.find((option: any) => option.default) && {
      [DefaultValueKey]: options.find((option) => option.default)?.value,
    }),
  };
}

function takeDropdownViewProperties(dropdown: Dropdown): object {
  const options = dropdown.options ?? [];
  return {
    [HideLabelKey]: !dropdown.showLabel,
    [AddOtherKey]: dropdown.customOption != null,
    [OptionsKey]: options.map((option: any) => makeOptionView(option)),
    ...(dropdown.description && { [DescriptionKey]: dropdown.description }),
    ...(dropdown.label && { [LabelKey]: dropdown.label }),
    ...(dropdown.placeholder && { [PlaceholderKey]: dropdown.placeholder }),
    ...(dropdown.customOption && {
      [AddOtherLabelKey]: dropdown.customOption.label,
      [AddOtherPlaceholderKey]: dropdown.customOption.placeholder,
    }),
    ...(options.find((option: any) => option.default) && {
      [DefaultValueKey]: options.find((option: any) => option.default)?.value,
    }),
  };
}

function takeDateTimeViewProperties(dateTimeInput: DateTimeInput): object {
  return {
    [HideLabelKey]: !dateTimeInput.showLabel,
    [ShowDateLabelsKey]: dateTimeInput.showDateLabels,
    [HidePlaceholderKey]: !dateTimeInput.showPlaceholder,
    ...(dateTimeInput.description && {
      [DescriptionKey]: dateTimeInput.description,
    }),
    ...(dateTimeInput.label && { [LabelKey]: dateTimeInput.label }),
    [Use24HourFormatKey]: dateTimeInput?.use24HourFormat,
  };
}

function takeDateInputViewProperties(dateInput: DateInput): object {
  return {
    [HideLabelKey]: !dateInput.showLabel,
    [ShowDateLabelsKey]: dateInput.showDateLabels,
    [HidePlaceholderKey]: !dateInput.showPlaceholder,
    ...(dateInput.description && {
      [DescriptionKey]: dateInput.description,
    }),
    ...(dateInput.label && { [LabelKey]: dateInput.label }),
  };
}

function takeTimeInputViewProperties(timeInput: TimeInput): object {
  return {
    [HideLabelKey]: !timeInput.showLabel,
    [HidePlaceholderKey]: !timeInput.showPlaceholder,
    ...(timeInput.description && {
      [DescriptionKey]: timeInput.description,
    }),
    ...(timeInput.label && { [LabelKey]: timeInput.label }),
    [Use24HourFormatKey]: timeInput.use24HourFormat,
  };
}

function takeDatePickerViewProperties(datePicker: DatePicker): object {
  return {
    [HideLabelKey]: !datePicker.showLabel,
    ...(datePicker.placeholder && { [PlaceholderKey]: datePicker.placeholder }),
    ...(datePicker.description && {
      [DescriptionKey]: datePicker.description,
    }),
    ...(datePicker.label && { [LabelKey]: datePicker.label }),
    [FirstDayOfWeekKey]: datePicker?.firstDayOfWeek,
  };
}

function takePhoneInputViewProperties(phoneInput: PhoneInput): object {
  return {
    [HideLabelKey]: !phoneInput.showLabel,
    ...(phoneInput.label && { [LabelKey]: phoneInput.label }),
    ...(phoneInput.placeholder && { [PlaceholderKey]: phoneInput.placeholder }),
    ...(phoneInput.description && { [DescriptionKey]: phoneInput.description }),
    ...(phoneInput.defaultCountryCode && {
      [DefaultCountryCodeKey]: phoneInput.defaultCountryCode,
    }),
    [ShowFlagKey]: phoneInput.showCountryFlag,
  };
}

function takeServiceDropdownViewProperties(
  servicesDropdownOptions: ServicesDropdown,
): object {
  const options = servicesDropdownOptions.options ?? [];
  return {
    [HideLabelKey]: !servicesDropdownOptions.showLabel,
    [OptionsKey]: options.map((option: any) => makeOptionView(option)),
    ...(servicesDropdownOptions.label && {
      [LabelKey]: servicesDropdownOptions.label,
    }),
    ...(servicesDropdownOptions.placeholder && {
      [PlaceholderKey]: servicesDropdownOptions.placeholder,
    }),
    ...(servicesDropdownOptions.customOption && {
      [AddOtherLabelKey]: servicesDropdownOptions.customOption.label,
      [AddOtherPlaceholderKey]:
        servicesDropdownOptions.customOption.placeholder,
    }),
  };
}

function resolvePaymentComponent(paymentComponent: Payment): object {
  switch (paymentComponent.componentType) {
    case PaymentComponentType.CHECKBOX_GROUP:
      return takeProductCheckboxGroupViewProperties(
        paymentComponent.checkboxGroupOptions!,
      );
    case PaymentComponentType.DONATION_INPUT:
      return takeDonationsInputViewProperties(
        paymentComponent.donationInputOptions!,
      );
    case PaymentComponentType.FIXED_PAYMENT:
      return takeFixedPaymentViewProperties(
        paymentComponent.fixedPaymentOptions!,
      );
    case PaymentComponentType.PAYMENT_INPUT:
      return takePaymentInputViewProperties(
        paymentComponent.paymentInputOptions!,
      );
    default:
      throw new Error(
        `Unknown payment component type: ${paymentComponent.componentType}`,
      );
  }
}

function resolveMultilineAddressComponent(multilineAddress: Address): object {
  switch (multilineAddress.componentType) {
    case AddressComponentType.MULTILINE_ADDRESS:
      return takeMultilineAddressViewProperties(
        multilineAddress.multilineAddressOptions!,
      );
    default:
      throw new Error(
        `Unknown multiline address component type: ${multilineAddress.componentType}`,
      );
  }
}

function resolveSchedulingComponent(schedulingOptions: Scheduling): object {
  switch (schedulingOptions.componentType) {
    case SchedulingComponentType.APPOINTMENT:
      return takeAppointmentViewProperties(
        schedulingOptions.appointmentOptions!,
      );
    default:
      throw new Error(
        `Unknown scheduling component type: ${schedulingOptions.componentType}`,
      );
  }
}

function takeMultilineAddressViewProperties(
  multilineAddress: MultilineAddress,
): object {
  const { showLabel, ...multilineAddressView } = multilineAddress;
  return { ...multilineAddressView, hideLabel: !showLabel };
}

function takeAppointmentViewProperties(
  appointmentOptions: Appointment,
): object {
  const {
    label,
    name,
    durationInMinutes,
    manualApprovalRequired,
    staffIds,
    format,
    description,
    showLabel,
    phoneOptions,
    videoConferenceOptions,
    inPersonOptions,
  } = appointmentOptions;
  return {
    ...(label && { [LabelKey]: label }),
    ...(name && { [NameKey]: name }),
    ...(durationInMinutes && { [DurationKey]: durationInMinutes }),
    ...(manualApprovalRequired !== undefined && {
      [ManualApprovalRequiredKey]: manualApprovalRequired,
    }),
    ...(description && { [DescriptionKey]: description }),
    ...(showLabel !== undefined && { [HideLabelKey]: !showLabel }),
    [FormatKey]: format,
    [FormatDescriptionTypeKey]: format,
    [StaffIdsKey]: staffIds,
    ...transformFormatInfoToView(
      inPersonOptions,
      videoConferenceOptions,
      phoneOptions,
    ),
  };
}

function transformFormatInfoToView(
  inPersonOptions?: InPersonOptions,
  videoConferenceOptions?: VideoConferenceOptions,
  phoneOptions?: PhoneOptions,
): object {
  if (phoneOptions?.description) {
    return { [FormatDescriptionKey]: phoneOptions.description };
  }
  if (videoConferenceOptions?.description) {
    return { [FormatDescriptionKey]: videoConferenceOptions.description };
  }
  if (inPersonOptions) {
    const { locations } = inPersonOptions;
    const customLocation = (locations ?? []).find((loc) => loc.customAddress);
    if (customLocation) {
      return {
        [FormatDescriptionKey]: customLocation.customAddress,
        [LocationTypeKey]: 'CUSTOM_LOCATION',
      };
    }
    const businessIds = (locations ?? [])
      .map((loc) => loc.businessLocationId)
      .filter(Boolean);
    if (businessIds.length > 0) {
      return {
        [LocationIdsKey]: businessIds,
        [LocationTypeKey]: 'OS_LOCATION',
      };
    }
  }
  return {};
}

function takeDonationsInputViewProperties(
  donationInput: DonationInput,
): object {
  const options = donationInput.options ?? [];
  return {
    [HideLabelKey]: !donationInput.showLabel,
    [AddOtherKey]: donationInput.customOption != null,
    [NumberOfColumnsKey]: numericNumberOfColumns(
      donationInput.numberOfColumns ?? NumberOfColumns.ONE,
    ),
    [OptionsKey]: options.map((option: any) => makeOptionView(option)),
    ...(donationInput.description && {
      [DescriptionKey]: donationInput.description,
    }),
    ...(donationInput.label && { [LabelKey]: donationInput.label }),
    ...(donationInput.customOption && {
      [AddOtherLabelKey]: donationInput.customOption.label,
      [AddOtherPlaceholderKey]: donationInput.customOption.placeholder,
    }),
    ...(options.find((option: any) => option.default) && {
      [DefaultValueKey]: options.find((option) => option.default)?.value,
    }),
  };
}

function takeFixedPaymentViewProperties(fixedPayment: FixedPayment): object {
  return {
    [HideLabelKey]: !fixedPayment.showLabel,
    ...(fixedPayment.label && { [LabelKey]: fixedPayment.label }),
    ...(fixedPayment.description && {
      [DescriptionKey]: fixedPayment.description,
    }),
  };
}

function takePaymentInputViewProperties(paymentInput: PaymentInput): object {
  return {
    [HideLabelKey]: !paymentInput.showLabel,
    ...(paymentInput.label && { [LabelKey]: paymentInput.label }),
    ...(paymentInput.description && {
      [DescriptionKey]: paymentInput.description,
    }),
    ...(paymentInput.placeholder && {
      [PlaceholderKey]: paymentInput.placeholder,
    }),
  };
}

function makeOptionView(option: any): Record<string, any> {
  return {
    [LabelKey]: option.label,
    [ValueKey]: option.value,
    [idKey]: option.id,
    ...(option.media && { [MediaKey]: option.media }),
  };
}

function numericNumberOfColumns(numberOfColumnsEnum: string): number {
  switch (numberOfColumnsEnum) {
    case 'TWO':
      return 2;
    case 'THREE':
      return 3;
    default:
      return 1;
  }
}

function resolveObjectArrayComponent(
  objectArrayComponent: ObjectArray,
): object {
  switch (objectArrayComponent.componentType) {
    case ObjectArrayComponentType.REPEATER:
      return takeRepeaterViewProperties(objectArrayComponent.repeaterOptions!);
    default:
      throw new Error(
        `Unknown object array component type: ${objectArrayComponent.componentType}`,
      );
  }
}

function takeRepeaterViewProperties(repeater: Repeater): object {
  return {
    [LabelKey]: repeater.label,
    [HideLabelKey]: !repeater.showLabel,
    [RepeaterFieldsKey]: repeater.fields?.map(transformFieldToV1),
    [RepeaterLayoutKey]: repeater.layout,
    ...(repeater.description && { [DescriptionKey]: repeater.description }),
  };
}
